import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { fetchLead, updateLead, deleteLead, scoreLead, fetchParameters, sendLeadToNegative } from '@/lib/api';
import { formatINR, formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Sparkles, ArrowLeft, Save, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { StageBadge } from './Leads';
import ActivityTimeline from '@/components/ActivityTimeline';

const NEG_REASONS = ["No Budget", "No Timing", "Wrong Fit", "No Response", "Competitor Won", "Duplicate", "Other"];

export default function LeadDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [lead, setLead] = useState(null);
    const [params, setParams] = useState({});
    const [scoring, setScoring] = useState(false);
    const [saving, setSaving] = useState(false);
    const [negOpen, setNegOpen] = useState(false);
    const [negReason, setNegReason] = useState('No Budget');

    useEffect(() => {
        fetchLead(id).then(setLead);
        fetchParameters().then(setParams);
    }, [id]);

    const save = async () => {
        setSaving(true);
        try {
            const updated = await updateLead(id, {
                customer_name: lead.customer_name, contact_person: lead.contact_person, email: lead.email, mobile: lead.mobile,
                stage: lead.stage, sales_owner: lead.sales_owner, service_line: lead.service_line, service_offered: lead.service_offered,
                proposal_value: Number(lead.proposal_value), notes: lead.notes, next_action: lead.next_action, priority: lead.priority,
                area: lead.area, location: lead.location, source: lead.source, expected_month: lead.expected_month,
                win_loss: lead.win_loss,
            });
            setLead(updated);
            toast.success('Lead updated');
        } catch { toast.error('Update failed'); }
        setSaving(false);
    };

    const runScore = async () => {
        setScoring(true);
        try {
            const r = await scoreLead(id);
            const l = await fetchLead(id);
            setLead(l);
            toast.success(`AI Score: ${r.score} · ${r.priority}`);
        } catch { toast.error('AI scoring failed'); }
        setScoring(false);
    };

    const del = async () => {
        if (!window.confirm('Delete this lead?')) return;
        await deleteLead(id);
        toast.success('Lead deleted');
        nav('/leads');
    };

    const sendToNeg = async () => {
        try {
            await sendLeadToNegative(id, negReason);
            toast.success(`Moved to Negative Leads (${negReason})`);
            nav('/negative-leads');
        } catch { toast.error('Move failed'); }
    };

    if (!lead) return <div className="p-8 text-slate-500">Loading…</div>;

    let aiParsed = null;
    if (lead.ai_suggestion) {
        try { aiParsed = JSON.parse(lead.ai_suggestion); }
        catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Failed to parse ai_suggestion JSON:', err?.message);
        }
    }

    return (
        <div>
            <PageHeader
                title={lead.customer_name}
                subtitle={`${lead.lead_id} · ${lead.industry || 'Unknown industry'}`}
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" className="border-[#E2E8F0] bg-transparent text-slate-900 hover:bg-[#FFFFFF]" onClick={() => nav('/leads')} data-testid="btn-back">
                            <ArrowLeft className="w-4 h-4" />
                        </Button>
                        <Button onClick={runScore} disabled={scoring} data-testid="btn-ai-score" className="bg-[#016a6d] hover:bg-[#014e50] gap-2">
                            <Sparkles className="w-4 h-4" /> {scoring ? 'Scoring…' : 'AI Score'}
                        </Button>
                        <Button onClick={save} disabled={saving} data-testid="btn-save" className="bg-[#2d6b3d] hover:bg-[#255c33] gap-2">
                            <Save className="w-4 h-4" /> Save
                        </Button>
                        <Dialog open={negOpen} onOpenChange={setNegOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" data-testid="btn-send-negative" className="border-[#FF9F0A]/50 bg-transparent text-[#FF9F0A] hover:bg-[#FF9F0A]/10 gap-2">
                                    <XCircle className="w-4 h-4" /> Negative
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-md">
                                <DialogHeader>
                                    <DialogTitle className="font-display text-xl">Move to Negative Leads</DialogTitle>
                                    <DialogDescription className="text-slate-500">
                                        This will remove <span className="text-slate-900">{lead.customer_name}</span> from your active pipeline and add it to the Negative Leads pool. You can transfer it back anytime.
                                    </DialogDescription>
                                </DialogHeader>
                                <div>
                                    <label className="overline block mb-1">Reason</label>
                                    <select value={negReason} onChange={e => setNegReason(e.target.value)} data-testid="send-neg-reason" className="w-full h-10 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-sm px-3">
                                        {NEG_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                </div>
                                <DialogFooter>
                                    <Button variant="outline" className="border-[#E2E8F0] bg-transparent text-slate-900" onClick={() => setNegOpen(false)}>Cancel</Button>
                                    <Button onClick={sendToNeg} data-testid="btn-confirm-send-negative" className="bg-[#FF9F0A] hover:bg-[#d98a0a] text-black">Move to Negative</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                        <Button variant="outline" className="border-[#8a2d2d] bg-transparent text-[#FF453A]" onClick={del} data-testid="btn-delete">
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                }
            />
            <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main */}
                <div className="lg:col-span-2 space-y-6">
                    <Section title="Deal">
                        <div className="grid grid-cols-2 gap-4">
                            <F label="Customer" value={lead.customer_name} onChange={v => setLead({ ...lead, customer_name: v })} testId="detail-customer" />
                            <F label="Industry" value={lead.industry} onChange={v => setLead({ ...lead, industry: v })} testId="detail-industry" />
                            <S label="Service Line" value={lead.service_line} options={params.service_lines} onChange={v => setLead({ ...lead, service_line: v })} testId="detail-service-line" />
                            <F label="Service Offered" value={lead.service_offered} onChange={v => setLead({ ...lead, service_offered: v })} testId="detail-service-offered" />
                            <F label="Proposal Value (₹)" type="number" value={lead.proposal_value} onChange={v => setLead({ ...lead, proposal_value: v })} testId="detail-value" />
                            <S label="Stage" value={lead.stage} options={params.stages} onChange={v => setLead({ ...lead, stage: v })} testId="detail-stage" />
                            <WinLossField value={lead.win_loss} stage={lead.stage} onChange={v => setLead({ ...lead, win_loss: v })} testId="detail-winloss" />
                            <S label="Sales Owner" value={lead.sales_owner} options={params.sales_owners} onChange={v => setLead({ ...lead, sales_owner: v })} testId="detail-owner" />
                            <S label="Priority" value={lead.priority} options={params.priorities} onChange={v => setLead({ ...lead, priority: v })} testId="detail-priority" />
                            <S label="Source" value={lead.source} options={params.sources} onChange={v => setLead({ ...lead, source: v })} testId="detail-source" />
                            <F label="Expected Month" value={lead.expected_month} onChange={v => setLead({ ...lead, expected_month: v })} testId="detail-month" />
                        </div>
                    </Section>

                    <Section title="Contact">
                        <div className="grid grid-cols-2 gap-4">
                            <F label="Contact Person" value={lead.contact_person} onChange={v => setLead({ ...lead, contact_person: v })} testId="detail-contact" />
                            <F label="Designation" value={lead.designation} onChange={v => setLead({ ...lead, designation: v })} testId="detail-designation" />
                            <F label="Email" value={lead.email} onChange={v => setLead({ ...lead, email: v })} testId="detail-email" />
                            <F label="Mobile" value={lead.mobile} onChange={v => setLead({ ...lead, mobile: v })} testId="detail-mobile" />
                            <F label="Location" value={lead.location} onChange={v => setLead({ ...lead, location: v })} testId="detail-location" />
                            <F label="Area" value={lead.area} onChange={v => setLead({ ...lead, area: v })} testId="detail-area" />
                        </div>
                    </Section>

                    <Section title="Notes & Next Action">
                        <div className="space-y-4">
                            <div>
                                <label className="overline block mb-1">Next Action</label>
                                <Textarea rows={2} value={lead.next_action || ''} onChange={e => setLead({ ...lead, next_action: e.target.value })} data-testid="detail-next-action" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900" />
                            </div>
                            <div>
                                <label className="overline block mb-1">Notes</label>
                                <Textarea rows={4} value={lead.notes || ''} onChange={e => setLead({ ...lead, notes: e.target.value })} data-testid="detail-notes" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900" />
                            </div>
                        </div>
                    </Section>

                    <ActivityTimeline leadId={id} />
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    <div className="surface-1 rounded-md p-6">
                        <div className="overline">Current Stage</div>
                        <div className="mt-2"><StageBadge stage={lead.stage} /></div>
                        <div className="grid grid-cols-2 gap-4 mt-6">
                            <Stat label="Proposal" value={formatINR(lead.proposal_value)} />
                            <Stat label="Weighted" value={formatINR(lead.weighted_value)} />
                            <Stat label="Probability" value={`${((lead.probability || 0) * 100).toFixed(0)}%`} />
                            <Stat label="Days Open" value={lead.days_in_pipeline} />
                        </div>
                        <div className="mt-4 pt-4 border-t border-[#E2E8F0] text-xs text-slate-500 space-y-1">
                            <div>Added: <span className="text-slate-700">{formatDate(lead.date_added)}</span></div>
                            <div>Expected Close: <span className="text-slate-700">{formatDate(lead.expected_close_date)}</span></div>
                        </div>
                    </div>

                    <div className="surface-1 rounded-md p-6 border-[#016a6d]/40" data-testid="ai-panel">
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-[#016a6d]" />
                            <div className="overline text-[#016a6d]">AI Priority Analysis</div>
                        </div>
                        {lead.ai_score != null ? (
                            <>
                                <div className="flex items-baseline gap-3 mt-3">
                                    <div className="font-display text-4xl font-bold text-slate-900 tabular-nums">{Math.round(lead.ai_score)}</div>
                                    <div className="text-xs text-slate-500">/ 100</div>
                                    <div className="ml-auto text-sm text-[#016a6d] font-medium">{aiParsed?.priority || lead.priority}</div>
                                </div>
                                {aiParsed?.reasoning && (
                                    <p className="text-sm text-slate-700 mt-3 leading-relaxed">{aiParsed.reasoning}</p>
                                )}
                                {aiParsed?.next_action && (
                                    <div className="mt-4 p-3 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md">
                                        <div className="overline mb-1">Recommended Action</div>
                                        <div className="text-sm text-slate-900">{aiParsed.next_action}</div>
                                    </div>
                                )}
                                <div className="text-[10px] text-slate-500 mt-3">Updated {formatDate(lead.ai_updated_at)}</div>
                            </>
                        ) : (
                            <p className="text-sm text-slate-500 mt-2">Run AI scoring to get a 0–100 priority score, reasoning, and a specific next-action recommendation powered by Claude Sonnet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="surface-1 rounded-md p-6">
            <div className="overline mb-4">{title}</div>
            {children}
        </div>
    );
}
function F({ label, value, onChange, type = 'text', testId }) {
    return (
        <div>
            <label className="overline block mb-1">{label}</label>
            <Input value={value || ''} type={type} onChange={e => onChange(e.target.value)} data-testid={testId} className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" />
        </div>
    );
}
function S({ label, value, options = [], onChange, testId }) {
    return (
        <div>
            <label className="overline block mb-1">{label}</label>
            <select value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testId} className="w-full h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-sm px-3">
                <option value="">—</option>
                {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}

// Win/Loss field: 4 fixed options with friendly labels but stable backend values.
// When the stage is a Closure stage, the backend forces this value — we lock the field.
const WIN_LOSS_OPTIONS = [
    { value: 'Won', label: 'Win' },
    { value: 'Lost', label: 'Lost' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Upcoming Months', label: 'Upcoming Month' },
];
function WinLossField({ value, stage, onChange, testId }) {
    const stageLocked = stage === 'Closure - Won' || stage === 'Closure - Lost';
    const color = value === 'Won' ? 'text-[#34C759]' : value === 'Lost' ? 'text-[#FF453A]' : value === 'Upcoming Months' ? 'text-[#FF9F0A]' : 'text-[#189290]';
    return (
        <div>
            <label className="overline block mb-1">Win / Loss {stageLocked && <span className="normal-case tracking-normal text-[10px] text-slate-500 ml-1">(locked by stage)</span>}</label>
            <select
                value={value || 'In Progress'}
                onChange={e => onChange(e.target.value)}
                disabled={stageLocked}
                data-testid={testId}
                className={`w-full h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-sm px-3 disabled:opacity-60 disabled:cursor-not-allowed font-medium ${color}`}
            >
                {WIN_LOSS_OPTIONS.map(o => <option key={o.value} value={o.value} className="text-slate-900">{o.label}</option>)}
            </select>
        </div>
    );
}
function Stat({ label, value }) {
    return (
        <div>
            <div className="overline">{label}</div>
            <div className="text-sm text-slate-900 tabular-nums font-medium mt-1">{value}</div>
        </div>
    );
}
